import React, {useCallback} from 'react';
import {Backdrop, Box, Button, CircularProgress, Paper, Typography} from '@mui/material';
import ErrorOutlinedIcon from '@mui/icons-material/ErrorOutlined';
import {ErrorBoundary as ReactErrorBoundary} from 'react-error-boundary';
import {reportError, isChunkLoadError, canReloadForChunkError, reloadForChunkError} from '../data/errorReporting';

function ErrorFallback(props) {
    const {error, resetErrorBoundary} = props;

    // For chunk load errors, show nothing while the page reloads. The fallback
    // renders before onError fires, so peek at the guard rather than consuming
    // an attempt — once the cap is hit there's no reload coming and the user
    // needs the error screen, not an endless spinner.
    if (isChunkLoadError(error) && canReloadForChunkError()) {
        return (
            <Backdrop open sx={{backgroundColor: 'background.default', zIndex: 9999}}>
                <CircularProgress />
            </Backdrop>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                backgroundColor: 'background.default',
                p: 3
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    p: 4,
                    maxWidth: 600,
                    textAlign: 'center'
                }}
            >
                <ErrorOutlinedIcon
                    sx={{
                        fontSize: 64,
                        color: 'error.main',
                        mb: 2
                    }}
                />
                <Typography variant="h4" gutterBottom>
                    Oops! Something went wrong
                </Typography>
                <Typography variant="body1" paragraph sx={{color: 'text.secondary'}}>
                    We&apos;re sorry for the inconvenience. The application encountered an unexpected error.
                    Our team has been notified and will look into this issue.
                </Typography>
                {error && (
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary',
                            fontFamily: 'monospace',
                            backgroundColor: 'grey.100',
                            p: 2,
                            borderRadius: 1,
                            mb: 2,
                            textAlign: 'left',
                            overflow: 'auto'
                        }}
                    >
                        {error.toString()}
                    </Typography>
                )}
                <Button
                    variant="contained"
                    color="primary"
                    onClick={resetErrorBoundary}
                    sx={{mt: 2}}
                >
                    Reload Application
                </Button>
            </Paper>
        </Box>
    );
}

function ErrorBoundary(props) {
    const {children} = props;

    const handleError = useCallback((error, errorInfo) => {
        // A lazily-imported chunk that fails to load or parse lands here rather
        // than on window.onerror, because React catches it during render.
        if (isChunkLoadError(error) && reloadForChunkError()) {
            return;
        }

        // Report non-chunk errors to Sentry
        reportError(error, {
            context: 'ErrorBoundary',
            componentStack: errorInfo.componentStack,
            errorBoundary: true
        });

        console.error('ErrorBoundary caught an error:', error, errorInfo);
    }, []);

    const handleReset = useCallback(() => {
        window.location.reload();
    }, []);

    return (
        <ReactErrorBoundary
            FallbackComponent={ErrorFallback}
            onError={handleError}
            onReset={handleReset}
        >
            {children}
        </ReactErrorBoundary>
    );
}

export default ErrorBoundary;
